/*
 * Sorting for the Admin Center "Manage Users" and "Employer Assignments" tables.
 * Both lists arrive complete from the gateway. The tables sort the whole set and
 * show it in batches. The number of rows already on screen stays when the sort
 * or the data changes.
 */

export const SORT_DIRECTIONS = Object.freeze({ ASC: 'asc', DESC: 'desc' })

export const USER_SORT_KEYS = Object.freeze({
  NAME: 'fullname',
  TITLE: 'role',
  EMPLOYERS: 'employerCount',
  INVITE_SENT: 'inviteSent'
})

export const EMPLOYER_SORT_KEYS = Object.freeze({
  STATUS: 'status',
  NAME: 'name',
  PLAN_START: 'planStartDate',
  FUNDING: 'medicalPlanFunding'
})

/* Current gateway order: users A→Z by name, employers by plan start newest first. */
export const DEFAULT_USER_SORT = Object.freeze({
  key: USER_SORT_KEYS.NAME,
  direction: SORT_DIRECTIONS.ASC
})

export const DEFAULT_EMPLOYER_SORT = Object.freeze({
  key: EMPLOYER_SORT_KEYS.PLAN_START,
  direction: SORT_DIRECTIONS.DESC
})

function compareNames(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'en', { sensitivity: 'base' })
}

function toMillis(value) {
  if (!value) return null
  const millis = new Date(value).getTime()
  return Number.isNaN(millis) ? null : millis
}

/* plan_start_date is stored as MM/DD/YYYY, which does not sort as text. */
function planStartMillis(value) {
  if (!value) return null
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value).trim())
  if (!match) return toMillis(value)
  const date = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]))
  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

/*
 * Missing values stay at the bottom in both directions. The sign flips only the
 * comparison of two real values.
 */
function compareMissingLast(aValue, bValue, sign) {
  if (aValue === null && bValue === null) return 0
  if (aValue === null) return 1
  if (bValue === null) return -1
  if (aValue < bValue) return -sign
  if (aValue > bValue) return sign
  return 0
}

function displayedTitle(user) {
  return user.role || 'N/a'
}

function compareUsers(a, b, key, sign) {
  switch (key) {
    case USER_SORT_KEYS.NAME:
      return sign * compareNames(a.fullname, b.fullname)
    case USER_SORT_KEYS.TITLE:
      return sign * compareNames(displayedTitle(a), displayedTitle(b))
    case USER_SORT_KEYS.EMPLOYERS:
      return sign * ((a.employerCount ?? 0) - (b.employerCount ?? 0))
    case USER_SORT_KEYS.INVITE_SENT:
      return compareMissingLast(
        toMillis(a.lastInviteSentAt || a.authCreatedAt),
        toMillis(b.lastInviteSentAt || b.authCreatedAt),
        sign
      )
    default:
      return 0
  }
}

function employerStatusLabel(employer) {
  return employer.isAssigned ? 'Assigned' : 'Unassigned'
}

function compareEmployers(a, b, key, sign) {
  switch (key) {
    case EMPLOYER_SORT_KEYS.STATUS:
      return sign * compareNames(employerStatusLabel(a), employerStatusLabel(b))
    case EMPLOYER_SORT_KEYS.NAME:
      return sign * compareNames(a.name, b.name)
    case EMPLOYER_SORT_KEYS.PLAN_START:
      return compareMissingLast(planStartMillis(a.planStartDate), planStartMillis(b.planStartDate), sign)
    case EMPLOYER_SORT_KEYS.FUNDING:
      return compareMissingLast(a.medicalPlanFunding || null, b.medicalPlanFunding || null, sign)
    default:
      return 0
  }
}

/**
 * @param {Array<Object>} rows
 * @param {{key: string, direction: string}} [sort]
 */
export function sortUserRows(rows = [], sort = DEFAULT_USER_SORT) {
  const sign = sort?.direction === SORT_DIRECTIONS.DESC ? -1 : 1
  return [...rows].sort((a, b) =>
    compareUsers(a, b, sort?.key, sign) || compareNames(a.fullname, b.fullname) || compareNames(a.id, b.id)
  )
}

/**
 * @param {Array<Object>} rows
 * @param {{key: string, direction: string}} [sort]
 */
export function sortEmployerRows(rows = [], sort = DEFAULT_EMPLOYER_SORT) {
  const sign = sort?.direction === SORT_DIRECTIONS.DESC ? -1 : 1
  return [...rows].sort((a, b) =>
    compareEmployers(a, b, sort?.key, sign) ||
    compareNames(a.name, b.name) ||
    compareNames(a.planYearId, b.planYearId)
  )
}
